/**
 * FASWORLD product discovery — auto-advance scroll-snap rows + Flash Sale center focus
 */
(() => {
  const DEFAULT_MS = 3500;
  const RESUME_MS = 12000;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const visibilityRows = new Set();

  if (!window.__fwDiscoveryVisibility) {
    window.__fwDiscoveryVisibility = true;
    document.addEventListener('visibilitychange', () => {
      visibilityRows.forEach(({ pauseNow, scheduleResume }) => {
        if (document.hidden) pauseNow();
        else scheduleResume();
      });
    });
  }

  const parseInterval = (section) => {
    const value = Number(section?.dataset?.fwDiscoveryInterval);
    return Number.isFinite(value) && value >= 2000 ? value : DEFAULT_MS;
  };

  const createRow = (row, options = {}) => {
    if (row.dataset.fwDiscoveryReady === 'true') return;

    const section = row.closest('[data-fw-discovery]');
    const track = row.querySelector('[data-fw-discovery-track]');
    if (!track || !section) return;

    const itemNodes = () => [...row.querySelectorAll('[data-fw-discovery-item]')];
    if (!itemNodes().length) return;

    row.dataset.fwDiscoveryReady = 'true';

    const focusMode = Boolean(options.focusMode);
    const autoMs = parseInterval(section);
    let currentIndex = 0;
    let autoTimer = null;
    let resumeTimer = null;
    let scrollTimer = null;
    let scrollIdleTimer = null;
    let hovering = false;
    let pointerDown = false;
    let scrolling = false;
    let focusFrame = 0;

    const isRtl = () => window.getComputedStyle(track).direction === 'rtl';

    const gap = () => {
      const styles = window.getComputedStyle(track);
      return Number.parseFloat(styles.columnGap || styles.gap) || 24;
    };

    const stepPx = () => {
      const first = itemNodes()[0];
      if (!first) return 0;
      return first.getBoundingClientRect().width + gap();
    };

    const maxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);

    const closestIndex = () => {
      const list = itemNodes();
      if (!list.length) return 0;

      if (!focusMode) {
        const step = stepPx();
        if (step <= 0) return 0;
        return Math.min(list.length - 1, Math.max(0, Math.round(Math.abs(track.scrollLeft) / step)));
      }

      const trackRect = track.getBoundingClientRect();
      const centerX = trackRect.left + trackRect.width / 2;
      let best = 0;
      let bestDist = Infinity;

      list.forEach((item, index) => {
        const rect = item.getBoundingClientRect();
        const dist = Math.abs(centerX - (rect.left + rect.width / 2));
        if (dist < bestDist) {
          bestDist = dist;
          best = index;
        }
      });

      return best;
    };

    const updateFocus = () => {
      if (!focusMode) return;
      const list = itemNodes();
      if (!list.length) return;
      const activeIndex = closestIndex();
      list.forEach((item, index) => {
        const active = index === activeIndex;
        item.classList.toggle('is-focused', active);
        if (active) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      });
    };

    const requestFocusUpdate = () => {
      if (!focusMode) return;
      window.cancelAnimationFrame(focusFrame);
      focusFrame = window.requestAnimationFrame(updateFocus);
    };

    const finishScroll = () => {
      if (!scrolling) return;
      window.clearTimeout(scrollTimer);
      scrolling = false;
      currentIndex = closestIndex();
      requestFocusUpdate();
    };

    const scrollToIndex = (index, behavior = 'smooth') => {
      const list = itemNodes();
      if (!list.length) return;

      const next = ((index % list.length) + list.length) % list.length;
      currentIndex = next;

      const motion = reduceMotion.matches ? 'auto' : behavior;
      const max = maxScroll();

      if (max <= 0 && !focusMode) {
        requestFocusUpdate();
        return;
      }

      scrolling = true;
      window.clearTimeout(scrollTimer);

      const sign = isRtl() ? -1 : 1;

      if (focusMode) {
        const target = list[next];
        const left = target.offsetLeft - (track.clientWidth - target.offsetWidth) / 2;
        const clamped = Math.max(0, Math.min(left, max || left));
        track.scrollTo({
          left: sign * clamped,
          behavior: motion,
        });
      } else {
        const step = stepPx();
        if (step <= 0) {
          scrolling = false;
          return;
        }
        track.scrollTo({ left: sign * Math.min(next * step, max), behavior: motion });
      }

      const fallbackMs = motion === 'auto' ? 80 : 480;
      scrollTimer = window.setTimeout(finishScroll, fallbackMs);
      window.setTimeout(requestFocusUpdate, motion === 'auto' ? 40 : 120);

      if ('onscrollend' in track) {
        track.addEventListener('scrollend', finishScroll, { once: true });
      }
    };

    const canAutoPlay = () =>
      !document.hidden &&
      !reduceMotion.matches &&
      !hovering &&
      !pointerDown &&
      !scrolling &&
      itemNodes().length >= 2 &&
      maxScroll() > 0;

    const advance = () => {
      if (!canAutoPlay()) return;
      scrollToIndex(currentIndex + 1);
    };

    const stopAuto = () => {
      window.clearInterval(autoTimer);
      autoTimer = null;
    };

    const startAuto = () => {
      stopAuto();
      if (!canAutoPlay()) return;
      autoTimer = window.setInterval(advance, autoMs);
    };

    const stopResume = () => {
      window.clearTimeout(resumeTimer);
      resumeTimer = null;
    };

    const pauseNow = () => {
      stopAuto();
      stopResume();
    };

    const scheduleResume = () => {
      stopResume();
      if (!canAutoPlay()) return;
      resumeTimer = window.setTimeout(startAuto, RESUME_MS);
    };

    const onManual = () => {
      pauseNow();
      scheduleResume();
      requestFocusUpdate();
    };

    const releasePointer = () => {
      if (!pointerDown) return;
      pointerDown = false;
      currentIndex = closestIndex();
      scheduleResume();
      requestFocusUpdate();
    };

    row.addEventListener('pointerenter', () => {
      hovering = true;
      pauseNow();
    });
    row.addEventListener('pointerleave', () => {
      hovering = false;
      scheduleResume();
    });
    row.addEventListener('focusin', pauseNow);
    row.addEventListener('focusout', (event) => {
      if (!row.contains(event.relatedTarget)) scheduleResume();
    });

    track.addEventListener(
      'pointerdown',
      () => {
        pointerDown = true;
        onManual();
      },
      { passive: true }
    );
    track.addEventListener('pointerup', releasePointer, { passive: true });
    track.addEventListener('pointercancel', releasePointer, { passive: true });
    track.addEventListener('lostpointercapture', releasePointer, { passive: true });

    track.addEventListener(
      'wheel',
      () => {
        if (maxScroll() <= 0) return;
        onManual();
      },
      { passive: true }
    );

    track.addEventListener(
      'scroll',
      () => {
        requestFocusUpdate();
        if (scrolling) return;
        window.clearTimeout(scrollIdleTimer);
        scrollIdleTimer = window.setTimeout(() => {
          currentIndex = closestIndex();
          requestFocusUpdate();
        }, 120);
      },
      { passive: true }
    );

    track.addEventListener('keydown', (event) => {
      const forwardKey = isRtl() ? 'ArrowLeft' : 'ArrowRight';
      const backwardKey = isRtl() ? 'ArrowRight' : 'ArrowLeft';
      if (event.key === forwardKey) {
        event.preventDefault();
        onManual();
        scrollToIndex(currentIndex + 1);
      }
      if (event.key === backwardKey) {
        event.preventDefault();
        onManual();
        scrollToIndex(currentIndex - 1);
      }
    });

    const layoutReady = () => {
      currentIndex = closestIndex();
      requestFocusUpdate();
      if (focusMode) scrollToIndex(currentIndex, 'auto');
      startAuto();
    };

    if ('ResizeObserver' in window) {
      new ResizeObserver(() => {
        requestFocusUpdate();
        if (maxScroll() <= 0) {
          pauseNow();
          return;
        }
        if (!autoTimer && !hovering && !pointerDown && !scrolling && !document.hidden) {
          scheduleResume();
        }
      }).observe(track);
    }

    visibilityRows.add({ pauseNow, scheduleResume });

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(layoutReady);
    });
  };

  const boot = (root = document) => {
    root.querySelectorAll('[data-fw-discovery]').forEach((section) => {
      const focusMode = section.hasAttribute('data-fw-discovery-focus');
      section.querySelectorAll('[data-fw-discovery-row]').forEach((row) => {
        createRow(row, { focusMode });
      });
    });
  };

  if (!window.__fwDiscoveryListeners) {
    window.__fwDiscoveryListeners = true;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
    } else {
      boot();
    }
    document.addEventListener('shopify:section:load', (event) => {
      boot(event.target);
    });
  } else if (document.readyState !== 'loading') {
    boot();
  }
})();
